import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { queryClient } from "./lib/query-client";
import { AuthProvider } from "./lib/auth";
import { AppRouter } from "./app/router";
import "./styles/globals.css";

// Check for service worker updates whenever the app regains focus.
// Mobile browsers keep tabs alive for days without a true navigation,
// so the default 24-hour SW update check is too slow after a deploy.
if ("serviceWorker" in navigator) {
  void navigator.serviceWorker.ready.then((registration) => {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        void registration.update();
      }
    });
  });
}

// When a new build deploys, the PWA service worker replaces cached chunks.
// If the browser still references old chunk filenames, the import fails.
// Auto-reload once to pick up the new build.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  if (!sessionStorage.getItem("vite:reload")) {
    sessionStorage.setItem("vite:reload", "1");
    window.location.reload();
  }
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,

    // Performance: sample 10% of transactions
    tracesSampleRate: 0.1,

    // Session replay: capture 0% normally, 100% on error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
  });
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="flex min-h-screen items-center justify-center p-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="mt-2 text-gray-600">
              {error instanceof Error ? error.message : "An unexpected error occurred."}
            </p>
            <button
              className="mt-4 rounded bg-black px-4 py-2 text-white"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
